import { inject, Injectable } from '@angular/core';
import { values } from 'lodash-es';
import { combineLatest, finalize, map, Observable, tap } from 'rxjs';

import { CanvasElementType, PropsOf } from '@blaze-runtime/engine-domain';
import {
  getBackground,
  makeContainerVisualEffectStyles,
  makeFontStyles,
  makeFontStyleStyles,
  PropertiesConfigInputContainerItemActionModel,
} from '@blaze-shared/elements-domain';
import {
  countedShareReplay,
  deepClone,
  deepMergeWithoutArrays,
  hashCodeFromObj,
  hashCodeFromString,
  isDefined,
} from '@blaze-shared/shared-util';
import { fromStyles } from '@blaze-shared/style-state-feature';
import { FontsService, ThemesService } from '@blaze-shared/theming-data-access';
import {
  STYLED_SLOT_SUPPORTED_ELEMENT_TYPES,
  StyleSheet,
  StyleSheetTarget,
} from '@blaze-shared/theming-domain';

import { ElementDefinitionsService } from '../element-definitions.service';
import { StylesCleanup } from '../styles-cleanup.service';

import { makeCssStringFromObj, STYLED_ACTION_GLOBAL_CLASS } from './style-css';

@Injectable({ providedIn: 'root' })
export class ElementStylesService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly fontsService = inject(FontsService);
  private readonly styleSheetsFacade = inject(fromStyles.StyleSheetsFacade);
  private readonly stylesCleanup = inject(StylesCleanup);
  private readonly themesService = inject(ThemesService);

  /**
   * Provides all style sheets including these that are inactive.
   * These should be used for references calculation.
   */
  readonly allSheets$ = this.styleSheetsFacade.entities$.pipe(
    map((sheetMap) => values(sheetMap).filter(isDefined)),
    countedShareReplay(),
  );

  /**
   * Provides style sheets that are active.
   * These can be assigned to elements and used to render the elements.
   *
   * Backend AI style sheets may carry a renamed `elementType` (the AI name, e.g.
   * 'row' / 'Number'); normalize it to the frontend element type (e.g. 'container'
   * / 'chartSingle') so element-to-style matching by `elementType` works.
   */
  readonly sheets$ = this.styleSheetsFacade.entities$.pipe(
    map((sheetMap) => {
      const sheets = values(sheetMap).filter(isDefined);
      return sheets
        .filter((styleSheet: StyleSheet) => styleSheet.active)
        .map((styleSheet) => this.normalizeSheetElementType(styleSheet));
    }),
    countedShareReplay(),
  );

  readonly slotStyleSheets$ = this.sheets$.pipe(
    map((sheets) =>
      sheets.filter((sheet) => STYLED_SLOT_SUPPORTED_ELEMENT_TYPES.includes(sheet.elementType)),
    ),
    countedShareReplay(),
  );

  observeStyleById(styleSheetId: string): Observable<StyleSheet | undefined> {
    return this.sheets$.pipe(map((sheets) => sheets.find((sheet) => sheet.id === styleSheetId)));
  }

  observeStyleByElementType(elementType: string): Observable<StyleSheet[]> {
    return this.sheets$.pipe(
      map((sheets) => sheets.filter((sheet) => sheet.elementType === elementType)),
    );
  }

  observeElementStyles(
    styleId: string | undefined,
    styleName: string | undefined,
    elementType: CanvasElementType,
  ): Observable<StyleSheet | undefined> {
    return this.sheets$.pipe(
      map((styleSheets) => {
        return fromStyles.findMatchingStyleSheet({
          id: styleId,
          name: styleName,
          elementType,
          sheets: styleSheets,
        });
      }),
    );
  }

  observeActionsStyles(
    elementActionsKey: string,
    elementActions$: Observable<PropertiesConfigInputContainerItemActionModel[]>,
    buttonClasses: string[],
  ): Observable<string> {
    this.stylesCleanup.register(elementActionsKey);
    const definition = this.elementDefinitionsService.getDefinition('button');
    const defaultProps = deepClone(definition?.defaults) || {};

    return combineLatest({
      styleSheets: this.sheets$,
      allActions: elementActions$,
    }).pipe(
      map(({ allActions, styleSheets }) => {
        if (!allActions.length) {
          return '';
        }

        const usedStyleSheets = new Set<StyleSheetTarget>();
        allActions.forEach(({ styleSheetId, styleSheetTarget }) => {
          if (styleSheetTarget?.id || styleSheetId) {
            usedStyleSheets.add({ id: styleSheetTarget?.id ?? styleSheetId });
          }
          if (styleSheetTarget?.name) {
            usedStyleSheets.add({ name: styleSheetTarget.name });
          }
        });

        return Array.from(usedStyleSheets).reduce((acc, styleSheetTarget) => {
          const styleSheet = fromStyles.findMatchingStyleSheet({
            id: styleSheetTarget.id,
            name: styleSheetTarget.name,
            elementType: 'button',
            sheets: styleSheets,
          });
          if (!styleSheet) {
            return acc;
          }

          const props = this.themesService.process(
            deepMergeWithoutArrays(defaultProps, styleSheet.data[0] || {}),
          ) as PropsOf<'button'> | undefined;

          const selectors = buttonClasses.map(
            (c) =>
              `.${elementActionsKey} .${c}.${this.getActionClasses(styleSheetTarget).join('.')}`,
          );
          return `${acc} ${this.makeStylesStringFromButtonElementProps(selectors, props)}`;
        }, '');
      }),
      tap((cssString) => {
        this.stylesCleanup.update(elementActionsKey, cssString);
      }),
      finalize(() => {
        this.stylesCleanup.dispose(elementActionsKey);
      }),
    );
  }

  getActionClasses(styleSheetTargetOrId: StyleSheetTarget | string | undefined): string[] {
    if (!styleSheetTargetOrId) {
      return [];
    }

    let hash: number;
    if (typeof styleSheetTargetOrId === 'string') {
      hash = hashCodeFromString(styleSheetTargetOrId);
    } else if (styleSheetTargetOrId.id && !styleSheetTargetOrId.name) {
      hash = hashCodeFromString(styleSheetTargetOrId.id);
    } else if (styleSheetTargetOrId.name && !styleSheetTargetOrId.id) {
      hash = hashCodeFromString(styleSheetTargetOrId.name);
    } else {
      hash = hashCodeFromObj(styleSheetTargetOrId);
    }

    return [STYLED_ACTION_GLOBAL_CLASS, `styled-action-${hash}`];
  }
  /** Maps a sheet's (possibly backend-renamed) `elementType` to the frontend type. */
  private normalizeSheetElementType(styleSheet: StyleSheet): StyleSheet {
    const resolvedType = this.elementDefinitionsService.resolveTypeByAiName(styleSheet.elementType);
    if (!resolvedType || resolvedType === styleSheet.elementType) {
      return styleSheet;
    }
    return { ...styleSheet, elementType: resolvedType };
  }

  private makeStylesStringFromButtonElementProps(
    selectors: string[],
    props: PropsOf<'button'> | undefined,
  ): string {
    if (!props) {
      return '';
    }

    const stylesPath = selectors.join(',');
    const hoverPath = selectors.map((s) => `${s}:not(:disabled):hover`).join(',');
    const disabledPath = selectors.map((s) => `${s}:disabled`).join(',');
    const { button, font, label } = props.style;

    if (!button || !font || !label) {
      return '';
    }

    const hasBorder = button.outline.selectorType !== 'none' && button.outlineWidth !== '0px';
    const hasBorderRadius = button.radius !== '0px';

    const styles: Record<string, string> = {
      color: label.color.color1,
      background: getBackground(button.fill),
      ...makeFontStyles(font),
      ...makeFontStyleStyles(font?.style, '700'),
      ['font-weight']: this.fontsService.getFontWeight(font.fontFamily, font.fontWeight),
      ...makeContainerVisualEffectStyles(button.visualEffect),
    };
    const hoverStyles: Record<string, string> = {
      color: label.color.color1,
    };
    const disabledStyles: Record<string, string> = {
      color: label.color.color1,
      opacity: '0.5',
    };

    if (hasBorder) {
      styles['border'] = `${button.outlineWidth} solid ${button.outline.color1}`;
      hoverStyles['border-color'] = button.outline.color1;
      disabledStyles['border-color'] = button.outline.color1;
    } else {
      styles['border-color'] = 'transparent';
      hoverStyles['border-color'] = 'transparent';
      disabledStyles['border-color'] = 'transparent';
    }

    if (hasBorderRadius) {
      styles['border-radius'] = button.radius;
    }

    return `
    ${stylesPath}{${makeCssStringFromObj(styles)}}
    ${hoverPath}{${makeCssStringFromObj(hoverStyles)}}
    ${disabledPath}{${makeCssStringFromObj(disabledStyles)}}
  `;
  }
}
